import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle, CheckCircle2, Edit2, Link, Loader2, MinusCircle, PlusCircle, ShieldCheck,
  Sparkles, Trash2, Upload, X, XCircle,
} from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import ConfirmModal from '@/components/shared/ConfirmModal';
import UploadField from '@/components/match/sessions/UploadField';
import { buildRecipeSummaryLines, recipeShortLabel } from '@/lib/match/recipeHelpers';
import { ALL_FIELDS, labelForTargetPath } from '@/lib/match/contractH';

const BASE = `${API_URL}/api/match`;

// Known transform options. Kept in sync with backend _apply_transform().
const TRANSFORM_OPTIONS = ['strip_usd_prefix', 'normalize_prc', 'parse_date', 'none'];

// Plain-English chip labels. The wire values above are the contract with n8n's
// applyTransform() and BOSS's _apply_transform() — only the presentation
// changes here. Tooltips spell out the actual behaviour so an operator can
// tell "Normalize category" from "Parse date" without reading the pipeline.
const TRANSFORM_LABELS = {
  strip_usd_prefix: 'Strip currency',
  normalize_prc: 'Normalize category',
  parse_date: 'Parse date',
  none: 'None',
};
const TRANSFORM_HINTS = {
  strip_usd_prefix: 'Removes "$", "USD" and thousands separators, leaving a plain number.',
  normalize_prc: 'Buckets the provider\'s wording (e.g. "Existing Customer-MACD") into New, MACD, Renewal or Upgrade.',
  parse_date: 'Converts common date formats (MM/DD/YYYY, YYYY-MM-DD) to ISO YYYY-MM-DD.',
  none: 'Passes the value through unchanged.',
};
const transformLabel = (t) => TRANSFORM_LABELS[t] || t;

// Target-path catalog for the mapping editor's dropdown — the Contract H
// field list with the template-side "output." prefix restored. Free-typed
// paths caused the drift class that broke live booking twice on 2026-07-10
// (contract_term_months, contract_start_date, opportunity junk → product);
// a picker makes the canonical spelling the default.
const TARGET_PATH_OPTIONS = ALL_FIELDS.map((f) => ({
  value: `output.${f.path}`,
  label: f.label,
  tab: f.tab,
}));
// Operators pick a business field, not a JSON path — the dropdown and the
// suggestion review list show the Contract H label only. Shared with
// MagicMapFlowModal via contractH.js so one field has one name everywhere;
// an unknown path still renders verbatim so an off-catalog target stays visible.
const targetPathLabel = labelForTargetPath;

const TARGET_TAB_LABELS = {
  top_level: 'Top Level',
  order_details: 'Order Details',
  order_attributes: 'Order Attributes',
  milestones: 'Milestones',
  service_attributes: 'Service Attributes',
};
const CUSTOM_PATH_SENTINEL = '__custom__';

function TargetPathSelect({ value, onChange }) {
  const known = TARGET_PATH_OPTIONS.some((o) => o.value === value);
  const [customMode, setCustomMode] = useState(Boolean(value) && !known);

  // Values arriving from outside (Magic Map apply, template load) that aren't
  // in the catalog must stay visible — flip to the free-text input.
  useEffect(() => {
    if (value && !TARGET_PATH_OPTIONS.some((o) => o.value === value)) {
      setCustomMode(true);
    }
  }, [value]);

  if (customMode) {
    return (
      <div className="flex gap-1 items-center">
        <input
          className="input-field w-full text-xs font-mono"
          placeholder="output.top_level.customer.name"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={() => setCustomMode(false)}
          className="shrink-0 text-[11px] text-boss-accent hover:text-boss-accent-hover transition"
          title="Pick from the Contract H field list instead"
        >
          list
        </button>
      </div>
    );
  }
  return (
    <select
      className="input-field w-full text-xs"
      value={known ? value : ''}
      onChange={(e) => {
        if (e.target.value === CUSTOM_PATH_SENTINEL) setCustomMode(true);
        else onChange(e.target.value);
      }}
    >
      <option value="" disabled>Pick a field…</option>
      {Object.entries(TARGET_TAB_LABELS).map(([tab, tabLabel]) => (
        <optgroup key={tab} label={tabLabel}>
          {TARGET_PATH_OPTIONS.filter((o) => o.tab === tab).map((o) => (
            <option key={o.value} value={o.value} title={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
      <option value={CUSTOM_PATH_SENTINEL}>Custom path…</option>
    </select>
  );
}

// ---------------------------------------------------------------------------
// Phase 4 deferral — client-side n8n suggestion translation shim.
//
// The server-side N8nClient.suggest_mappings() translator has NOT been built
// yet (deferred to Phase 4). Until it lands, the n8n /webhook/suggest-mappings
// endpoint returns the legacy MATCH source shape:
//   { provider_column, appdirect_field, transform (singular) }
//
// This shim translates that into the BOSS canonical ColumnMapping shape:
//   { source_column, target_path, transforms[] (list), required }
//
// REMOVAL NOTE: when the server-side translator in N8nClient.suggest_mappings()
// is implemented (Phase 4), delete this function and the call site below — the
// backend will return the canonical shape directly. Search for
// "translateN8nSuggestions" and "Phase 4 deferral" to find both locations.
// ---------------------------------------------------------------------------
export function translateN8nSuggestions(rawSuggestions) {
  return (rawSuggestions || []).map((s) => ({
    source_column: s.provider_column ?? s.source_column ?? '',
    target_path:   s.appdirect_field ?? s.target_path ?? '',
    transforms:    s.transforms ?? (s.transform ? [s.transform] : []),
    required:      Boolean(s.required ?? false),
  }));
}

// ---------------------------------------------------------------------------
// Preflight — tests a provider template's field mappings against the live
// AppDirect provider-sales API without submitting an order. Shared between
// MagicMapFlowModal (auto-runs right after Magic Mapping creates a template)
// and the "Preflight" action on an existing template's card below.
//
// Four Nextiva templates shipped with invalid field mappings and nobody knew
// until orders failed in production — this is the guard that proves a
// template works before an operator trusts it.
// ---------------------------------------------------------------------------

// The backend's exact error_code string was still being finalized as this
// was written, so detect session expiry defensively: trust the marker if
// it's there, but a bare 401/403 with no body counts too.
function isAuthExpired(res, data) {
  if (res.status === 401 || res.status === 403) return true;
  const code = data?.error_code ?? data?.detail?.error_code;
  return code === 'appdirect_auth_expired';
}

// Three outcomes, not two. The backend emits per-check status 'pass', 'fail'
// or 'skipped' (see match_provider_template.py's preflight checks). A skipped
// check is one that could not be evaluated — not one that failed — and
// collapsing it into "fail" would report a template as broken when it is
// merely unverified in that respect. On the surface whose whole job is earning
// trust in Magic Mapping's output, overstating failure is its own bug.
function checkOutcome(c) {
  for (const k of ['pass', 'passed', 'ok', 'success']) {
    if (typeof c[k] === 'boolean') return c[k] ? 'pass' : 'fail';
  }
  if (typeof c.status === 'string') {
    const s = c.status.toLowerCase();
    if (['pass', 'passed', 'ok', 'success'].includes(s)) return 'pass';
    if (['skip', 'skipped', 'n/a', 'na', 'not_applicable'].includes(s)) return 'skipped';
    return 'fail';
  }
  return 'fail';
}

// The per-check response shape wasn't locked down either — read name/reason
// generically from whichever keys are present instead of hard-coding fields
// that might not match what the backend actually sends.
function normalizeChecks(data) {
  const raw = Array.isArray(data) ? data : data?.checks ?? data?.results ?? [];
  return raw.map((c, i) => {
    const outcome = checkOutcome(c);
    return {
      name: c.name ?? c.check ?? c.id ?? `Check ${i + 1}`,
      outcome,
      pass: outcome !== 'fail',
      reason: c.reason ?? c.message ?? c.detail ?? c.description ?? '',
    };
  });
}

export async function runProviderTemplatePreflight(templateId) {
  try {
    const res = await api.post(`${BASE}/provider-templates/${templateId}/preflight`);
    const data = await res.json();
    if (!res.ok) {
      if (isAuthExpired(res, data)) return { status: 'auth_expired', checks: [], message: null };
      return { status: 'error', checks: [], message: data?.detail || 'Preflight check could not run.' };
    }
    const checks = normalizeChecks(data);
    // Only a genuine 'fail' downgrades the overall result; skipped checks
    // leave it green but are still rendered individually so nothing is hidden.
    const failed = checks.filter((c) => c.outcome === 'fail');
    return { status: failed.length ? 'fail' : 'ok', checks, message: null };
  } catch (exc) {
    return { status: 'error', checks: [], message: `Preflight check could not run: ${exc?.message || exc}` };
  }
}

// Saves a raw session paste through the same full-document contract
// SettingsTab itself uses — PUT /api/match/settings takes the whole settings
// object, not a partial patch. The paste is sent verbatim; the server parses
// it (services/match/cookie_paste.py), so nothing here splits or cleans it.
export async function saveAppDirectCookie(rawPaste) {
  const getRes = await api.get(`${BASE}/settings`);
  const settings = await getRes.json();
  if (!getRes.ok) {
    return { ok: false, message: settings?.detail || 'Could not load current settings.' };
  }
  const next = {
    ...settings,
    appdirect: {
      ...(settings.appdirect ?? {}),
      auth: { ...(settings.appdirect?.auth ?? {}), cookie: rawPaste },
    },
  };
  const putRes = await api.put(`${BASE}/settings`, next);
  const data = await putRes.json();
  if (!putRes.ok) {
    return { ok: false, message: data?.detail || 'Failed to save session.' };
  }
  return { ok: true };
}

// Renders whatever preflight state the caller is in — running, needing a
// fresh session, errored, or the actual per-check results. All-pass reads as
// reassurance on purpose: this is the moment Magic Mapping earns trust or
// doesn't. A failure names the check and what to do about it; never a bare
// "preflight failed".
export function PreflightPanel({
  preflight,
  cookiePaste,
  onCookiePasteChange,
  cookieSaving,
  cookieError,
  onCookieSave,
  onRetry,
}) {
  if (!preflight || preflight.status === 'idle') return null;

  if (preflight.status === 'running') {
    return (
      <div
        className="flex items-center gap-2 text-xs text-boss-text-secondary px-3 py-2.5 rounded-lg bg-boss-elevated border border-boss-border"
        data-testid="preflight-running"
      >
        <Loader2 size={13} className="animate-spin text-boss-accent" />
        Verifying against AppDirect…
      </div>
    );
  }

  if (preflight.status === 'auth_expired') {
    return (
      <div
        className="space-y-2 rounded-lg border border-[var(--boss-warning-border)] bg-boss-status-warn-bg px-3 py-3"
        data-testid="preflight-auth-expired"
      >
        <div className="flex items-start gap-2 text-xs text-boss-status-warn">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            AppDirect rejected the check — the session has expired. Sessions are
            short-lived (usually under an hour), so seeing this again soon is
            expected, not a bug. Paste a fresh one below.
          </span>
        </div>
        <textarea
          className="input-field w-full text-xs font-mono"
          rows={3}
          placeholder="Paste the Cookie header, Copy as cURL, or the DevTools → Application → Cookies rows"
          value={cookiePaste}
          onChange={(e) => onCookiePasteChange(e.target.value)}
          data-testid="preflight-cookie-paste"
        />
        {cookieError && (
          <p className="text-xs text-boss-status-danger" data-testid="preflight-cookie-error">
            {cookieError}
          </p>
        )}
        <button
          type="button"
          className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
          onClick={onCookieSave}
          disabled={cookieSaving}
          data-testid="preflight-cookie-save-btn"
        >
          {cookieSaving ? 'Saving…' : 'Save & Retry'}
        </button>
      </div>
    );
  }

  if (preflight.status === 'error') {
    return (
      <div
        className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg bg-boss-status-warn-bg border border-[var(--boss-warning-border)] text-boss-status-warn text-xs"
        data-testid="preflight-error"
      >
        <span className="flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {preflight.message}
        </span>
        <button type="button" onClick={onRetry} className="underline hover:no-underline shrink-0">
          Retry
        </button>
      </div>
    );
  }

  const allPass = preflight.status === 'ok';
  const failCount = preflight.checks.filter((c) => c.outcome === 'fail').length;
  const skipCount = preflight.checks.filter((c) => c.outcome === 'skipped').length;
  return (
    <div className="space-y-2" data-testid="preflight-results">
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${
          allPass
            ? 'bg-boss-status-success-bg border-[var(--boss-success-border)] text-boss-status-success'
            : 'bg-boss-status-warn-bg border-[var(--boss-warning-border)] text-boss-status-warn'
        }`}
        data-testid="preflight-summary"
      >
        {allPass ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
        {allPass
          ? skipCount
            ? `Verified against AppDirect — nothing failed, ${skipCount} check${skipCount !== 1 ? 's' : ''} could not be evaluated.`
            : 'Verified against AppDirect — every check passed.'
          : `${failCount} of ${preflight.checks.length} check${preflight.checks.length !== 1 ? 's' : ''} failed — review before relying on this template.`}
      </div>
      <div className="space-y-1.5">
        {preflight.checks.map((c, i) => (
          <div
            key={i}
            className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${
              c.outcome === 'fail'
                ? 'bg-boss-status-danger-bg border-[var(--boss-danger-border)]'
                : 'bg-boss-elevated border-boss-border'
            }`}
            data-testid={`preflight-check-${i}`}
          >
            {c.outcome === 'pass' && (
              <CheckCircle2 size={13} className="text-boss-status-success mt-0.5 shrink-0" />
            )}
            {c.outcome === 'fail' && (
              <XCircle size={13} className="text-boss-status-danger mt-0.5 shrink-0" />
            )}
            {/* Skipped is deliberately neither a tick nor a cross — the check
                did not run, so claiming either would be a lie. */}
            {c.outcome === 'skipped' && (
              <MinusCircle size={13} className="text-boss-text-muted mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <p
                className={`font-medium ${
                  c.outcome === 'fail'
                    ? 'text-boss-status-danger'
                    : c.outcome === 'skipped'
                      ? 'text-boss-text-muted'
                      : 'text-boss-text'
                }`}
              >
                {c.name}
                {c.outcome === 'skipped' && (
                  <span className="ml-1.5 font-normal text-boss-text-muted">(not checked)</span>
                )}
              </p>
              {c.reason && (
                <p
                  className={
                    c.outcome === 'fail'
                      ? 'text-boss-status-danger mt-0.5'
                      : 'text-boss-text-secondary mt-0.5'
                  }
                >
                  {c.reason}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

// Mapping rows carry a UI-only _uid so React keys are STABLE across
// delete/reorder — keying by index reused TargetPathSelect instances (and
// their customMode state) for a DIFFERENT mapping after a delete, making a
// catalog-mapped row render as free text (PR #446 review). formToBody's
// explicit field list strips _uid before save.
let _mappingUidCounter = 0;
function withMappingUid(m) {
  return m && m._uid ? m : { ...m, _uid: `map_${++_mappingUidCounter}` };
}

function emptyMapping() {
  return withMappingUid({ source_column: '', target_path: '', transforms: [], required: false });
}

function emptyRule() {
  return { name: '', condition_path: '', condition_op: '==', condition_value: '', action_path: '', action_value: '' };
}

function emptyTemplate() {
  return {
    name: '',
    default_product: '',
    sales_rep_priority: '',
    default_order_status: '',
    column_mappings: [],
    business_rules: [],
    appdirect_advisor_id: '',
    appdirect_product_id: '',
    appdirect_provider_id: '',
    appdirect_account_manager_id: '',
    parse_recipe: null,
  };
}

// Convert template record -> form state (stringify numeric IDs to avoid empty-string issues)
function templateToForm(t) {
  return {
    name: t.name || '',
    default_product: t.default_product || '',
    sales_rep_priority: t.sales_rep_priority || '',
    default_order_status: t.default_order_status || '',
    column_mappings: (t.column_mappings || []).map((m) => withMappingUid({
      source_column: m.source_column || '',
      target_path: m.target_path || '',
      transforms: Array.isArray(m.transforms) ? m.transforms : [],
      required: Boolean(m.required),
    })),
    business_rules: (t.business_rules || []).map((r) => ({
      name: r.name || '',
      condition_path: r.condition_path || '',
      condition_op: r.condition_op || '==',
      condition_value: r.condition_value ?? '',
      action_path: r.action_path || '',
      action_value: r.action_value ?? '',
    })),
    appdirect_advisor_id: t.appdirect_advisor_id != null ? String(t.appdirect_advisor_id) : '',
    appdirect_product_id: t.appdirect_product_id != null ? String(t.appdirect_product_id) : '',
    appdirect_provider_id: t.appdirect_provider_id != null ? String(t.appdirect_provider_id) : '',
    appdirect_account_manager_id: t.appdirect_account_manager_id != null ? String(t.appdirect_account_manager_id) : '',
    // Preserve the parse_recipe verbatim — read-only in the UI this pass
    parse_recipe: t.parse_recipe ?? null,
  };
}

// Convert form state -> API body (parse numeric ID strings back to numbers or null)
function formToBody(form) {
  function toIntOrNull(v) {
    const n = parseInt(v, 10);
    return isNaN(n) ? null : n;
  }
  return {
    name: form.name,
    default_product: form.default_product || null,
    sales_rep_priority: form.sales_rep_priority || null,
    default_order_status: form.default_order_status || null,
    column_mappings: form.column_mappings.map((m) => ({
      source_column: m.source_column,
      target_path: m.target_path,
      transforms: m.transforms.filter((t) => t && t !== 'none'),
      required: Boolean(m.required),
    })),
    business_rules: form.business_rules.map((r) => ({
      name: r.name,
      condition_path: r.condition_path,
      condition_op: r.condition_op,
      condition_value: r.condition_value,
      action_path: r.action_path,
      action_value: r.action_value,
    })),
    appdirect_advisor_id: toIntOrNull(form.appdirect_advisor_id),
    appdirect_product_id: toIntOrNull(form.appdirect_product_id),
    appdirect_provider_id: toIntOrNull(form.appdirect_provider_id),
    appdirect_account_manager_id: toIntOrNull(form.appdirect_account_manager_id),
    // Pass parse_recipe through VERBATIM if present (null omits the field from the patch)
    ...(form.parse_recipe != null ? { parse_recipe: form.parse_recipe } : {}),
  };
}

// ---------------------------------------------------------------------------
// Column Mappings Editor — used inside the Edit Modal
// ---------------------------------------------------------------------------

function ColumnMappingsEditor({ mappings, onChange }) {
  function updateRow(idx, field, value) {
    const next = mappings.map((m, i) => (i === idx ? { ...m, [field]: value } : m));
    onChange(next);
  }

  function toggleTransform(idx, transform) {
    const m = mappings[idx];
    const current = m.transforms || [];
    const updated = current.includes(transform)
      ? current.filter((t) => t !== transform)
      : [...current, transform];
    updateRow(idx, 'transforms', updated);
  }

  function addRow() { onChange([...mappings, emptyMapping()]); }
  function removeRow(idx) { onChange(mappings.filter((_, i) => i !== idx)); }

  return (
    <div className="space-y-2">
      {mappings.length === 0 && (
        <p className="text-xs text-boss-text-muted py-2">No mappings yet. Add one below.</p>
      )}
      {mappings.map((m, idx) => (
        <div key={m._uid || idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start p-3 rounded-lg bg-boss-elevated border border-boss-border">
          <div className="space-y-1">
            <label className="block text-[10px] text-boss-text-muted uppercase tracking-wide">Source Column</label>
            <input
              className="input-field w-full text-xs"
              placeholder="CSV header name"
              value={m.source_column}
              onChange={(e) => updateRow(idx, 'source_column', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-[10px] text-boss-text-muted uppercase tracking-wide">
              Target Field{' '}
              <span
                className="text-boss-accent cursor-default"
                title="Where this report column lands on the outgoing AppDirect record (Contract H path)"
              >
                (?)
              </span>
            </label>
            <TargetPathSelect
              value={m.target_path}
              onChange={(v) => updateRow(idx, 'target_path', v)}
            />
          </div>
          <button
            type="button"
            onClick={() => removeRow(idx)}
            className="mt-5 p-1 rounded text-boss-text-muted hover:text-boss-status-danger hover:bg-boss-status-danger-bg transition"
          >
            <X size={14} />
          </button>
          {/* Transforms + Required — full-width row beneath the two inputs */}
          <div className="col-span-3 flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[10px] text-boss-text-muted uppercase tracking-wide shrink-0">Transforms:</span>
            {TRANSFORM_OPTIONS.filter((t) => t !== 'none').map((t) => (
              <button
                key={t}
                type="button"
                title={TRANSFORM_HINTS[t]}
                onClick={() => toggleTransform(idx, t)}
                className={`px-2 py-0.5 rounded text-[11px] border transition ${
                  m.transforms.includes(t)
                    ? 'bg-boss-accent-soft border-boss-border-active text-boss-accent'
                    : 'bg-boss-surface border-boss-border text-boss-text-muted hover:border-boss-border-hover'
                }`}
              >
                {transformLabel(t)}
              </button>
            ))}
            <label className="ml-auto flex items-center gap-1 text-xs text-boss-text-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Boolean(m.required)}
                onChange={(e) => updateRow(idx, 'required', e.target.checked)}
                className="rounded border-boss-border"
              />
              Required
            </label>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-boss-accent hover:text-boss-accent-hover transition px-1 py-0.5"
      >
        <PlusCircle size={13} /> Add Mapping
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business Rules Editor — used inside the Edit Modal
// ---------------------------------------------------------------------------

function BusinessRulesEditor({ rules, onChange }) {
  function updateRow(idx, field, value) {
    const next = rules.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    onChange(next);
  }
  function addRow() { onChange([...rules, emptyRule()]); }
  function removeRow(idx) { onChange(rules.filter((_, i) => i !== idx)); }

  return (
    <div className="space-y-2">
      {rules.length === 0 && (
        <p className="text-xs text-boss-text-muted py-2">No business rules yet. Add one below.</p>
      )}
      {rules.map((r, idx) => (
        <div key={idx} className="p-3 rounded-lg bg-boss-elevated border border-boss-border space-y-2">
          <div className="flex items-center gap-2">
            <input
              className="input-field flex-1 text-xs"
              placeholder="Rule name"
              value={r.name}
              onChange={(e) => updateRow(idx, 'name', e.target.value)}
            />
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="p-1 rounded text-boss-text-muted hover:text-boss-status-danger hover:bg-boss-status-danger-bg transition"
            >
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-boss-text-muted mb-1">Condition Path</label>
              <input
                className="input-field w-full text-xs font-mono"
                placeholder="output.customer.type"
                value={r.condition_path}
                onChange={(e) => updateRow(idx, 'condition_path', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] text-boss-text-muted mb-1">Operator</label>
              <select
                className="input-field w-full text-xs"
                value={r.condition_op}
                onChange={(e) => updateRow(idx, 'condition_op', e.target.value)}
              >
                {['==', '!=', '>', '<', '>=', '<=', 'contains', 'startsWith'].map((op) => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-boss-text-muted mb-1">Condition Value</label>
              <input
                className="input-field w-full text-xs"
                placeholder="Enterprise"
                value={r.condition_value}
                onChange={(e) => updateRow(idx, 'condition_value', e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-boss-text-muted mb-1">Action Path</label>
              <input
                className="input-field w-full text-xs font-mono"
                placeholder="output.order_type"
                value={r.action_path}
                onChange={(e) => updateRow(idx, 'action_path', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] text-boss-text-muted mb-1">Action Value</label>
              <input
                className="input-field w-full text-xs"
                placeholder="Enterprise_Order"
                value={r.action_value}
                onChange={(e) => updateRow(idx, 'action_value', e.target.value)}
              />
            </div>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-boss-accent hover:text-boss-accent-hover transition px-1 py-0.5"
      >
        <PlusCircle size={13} /> Add Rule
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Magic Mapping sub-panel — lives inside the Column Mappings section of the
// Edit Modal. Calls /suggest-mappings, runs the Phase-4 translation shim,
// and lets the user merge or overwrite existing mappings.
//
// Provider Report source: either paste a URL or upload a CSV file directly
// (the uploaded file's bearer-serve URL is fed into suggest-mappings, so n8n
// can fetch it the same way it would a manually pasted URL).
// ---------------------------------------------------------------------------

const MM_INPUT_URL    = 'url';
const MM_INPUT_UPLOAD = 'upload';

function MagicMappingPanel({ onApply, onClose }) {
  const [inputMode,    setInputMode]    = useState(MM_INPUT_URL);
  const [reportUrl,    setReportUrl]    = useState('');
  const [uploadedInfo, setUploadedInfo] = useState(null); // { url, filename, ... }
  const [customersUrl, setCustomersUrl] = useState('');
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [suggestions,  setSuggestions]  = useState(null);
  // parse_recipe from the same suggest-mappings response. LIVE BUG
  // (2026-07-10): this panel dropped the recipe on the floor, so a template
  // built here couldn't parse the raw grouped report it was mapped FROM —
  // "Live fetch has nothing to search on" at import time.
  const [parseRecipe,  setParseRecipe]  = useState(null);

  // The effective provider-report URL for the API call
  const effectiveReportUrl =
    inputMode === MM_INPUT_UPLOAD ? (uploadedInfo?.url || '') : reportUrl;

  async function handleFetch() {
    if (!effectiveReportUrl.trim()) {
      setError(
        inputMode === MM_INPUT_UPLOAD
          ? 'Upload a report file first.'
          : 'Provider Report URL is required.',
      );
      return;
    }
    setLoading(true);
    setError(null);
    setSuggestions(null);
    try {
      const body = { provider_report_url: effectiveReportUrl };
      if (customersUrl.trim()) body.snapshot_customers_url = customersUrl.trim();
      const res = await api.post(`${BASE}/provider-templates/suggest-mappings`, body);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || 'Suggest-mappings request failed.');
        return;
      }
      // Envelope tolerance: the rewritten n8n workflow responds
      // { column_mappings: [...] } (BOSS-canonical items); the legacy v3
      // workflow returned a bare array of legacy-shaped items. Accept both —
      // translateN8nSuggestions() is already per-item shape-tolerant.
      const rawList = Array.isArray(data)
        ? data
        : data?.column_mappings ?? data?.suggestions ?? [];
      const translated = translateN8nSuggestions(rawList);
      if (translated.length === 0) {
        setError('n8n returned no mapping suggestions for this report.');
        return;
      }
      setSuggestions(translated);
      // Raw grouped reports come back with a parse_recipe; flat CSVs omit it.
      setParseRecipe(data?.parse_recipe ?? null);
    } catch (exc) {
      // Without this catch a shape mismatch died silently (loading spinner
      // stops, nothing happens). Surface it instead.
      setError(`Magic Mapping failed: ${exc?.message || exc}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-lg border border-boss-border bg-boss-accent-muted p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-boss-accent text-sm font-medium">
          <Sparkles size={14} />
          Magic Mapping
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-boss-text-muted hover:text-boss-text-secondary transition"
        >
          <X size={14} />
        </button>
      </div>

      {!suggestions ? (
        <div className="space-y-3">
          {/* Source mode toggle */}
          <div className="flex items-center gap-1 rounded-lg bg-boss-elevated border border-boss-border p-0.5 w-fit">
            <button
              type="button"
              onClick={() => setInputMode(MM_INPUT_URL)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition ${
                inputMode === MM_INPUT_URL
                  ? 'bg-boss-surface text-boss-text shadow-sm'
                  : 'text-boss-text-muted hover:text-boss-text-secondary'
              }`}
            >
              <Link size={11} />
              Paste URL
            </button>
            <button
              type="button"
              onClick={() => setInputMode(MM_INPUT_UPLOAD)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition ${
                inputMode === MM_INPUT_UPLOAD
                  ? 'bg-boss-surface text-boss-text shadow-sm'
                  : 'text-boss-text-muted hover:text-boss-text-secondary'
              }`}
            >
              <Upload size={11} />
              Upload CSV
            </button>
          </div>

          {/* Provider Report input */}
          {inputMode === MM_INPUT_URL ? (
            <div>
              <label className="block text-xs text-boss-text-muted mb-1">Provider Report URL</label>
              <input
                className="input-field w-full text-xs"
                placeholder="https://..."
                value={reportUrl}
                onChange={(e) => setReportUrl(e.target.value)}
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs text-boss-text-muted mb-1">Provider Report CSV</label>
              <UploadField
                label="Provider Report"
                endpoint="/api/match/uploads/provider-report"
                onChange={(info) => setUploadedInfo(info)}
              />
            </div>
          )}

          {/* Snapshot Customers URL — optional (server-side change made it no longer required) */}
          <div>
            <label className="block text-xs text-boss-text-muted mb-1">
              Snapshot Customers URL{' '}
              <span className="text-boss-text-muted font-normal">(optional)</span>
            </label>
            <input
              className="input-field w-full text-xs"
              placeholder="https://..."
              value={customersUrl}
              onChange={(e) => setCustomersUrl(e.target.value)}
            />
          </div>

          {error && (
            <p className="text-xs text-boss-status-danger">{error}</p>
          )}
          <button
            type="button"
            onClick={handleFetch}
            disabled={loading}
            className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
          >
            {loading ? 'Asking n8n…' : 'Get Suggestions'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-boss-text-secondary">
            n8n suggested <strong>{suggestions.length}</strong> mapping{suggestions.length !== 1 ? 's' : ''}.
          </p>
          <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
            {suggestions.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                <span className="text-boss-text-secondary font-mono flex-1 truncate">{s.source_column || '—'}</span>
                <span className="text-boss-text-muted">→</span>
                <span className="text-boss-accent flex-1 truncate" title={s.target_path}>
                  {s.target_path ? targetPathLabel(s.target_path) : '—'}
                </span>
                {s.transforms?.length > 0 && (
                  <span className="text-boss-text-muted text-[10px]">
                    [{s.transforms.map(transformLabel).join(', ')}]
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { onApply(suggestions, 'overwrite', parseRecipe); setSuggestions(null); }}
              className="btn-primary text-xs px-3 py-1.5"
            >
              Overwrite
            </button>
            <button
              type="button"
              onClick={() => { onApply(suggestions, 'merge', parseRecipe); setSuggestions(null); }}
              className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-boss-border bg-boss-elevated hover:bg-boss-surface text-boss-text transition"
            >
              Merge
            </button>
            <button
              type="button"
              onClick={() => setSuggestions(null)}
              className="text-xs px-3 py-1.5 text-boss-text-muted hover:text-boss-text-secondary transition"
            >
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Test Template Modal — uploads a sample CSV, shows row-by-row preview
// ---------------------------------------------------------------------------

function TestTemplateModal({ template, onClose }) {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function handleTest() {
    const file = fileRef.current?.files?.[0];
    if (!file) { setError('Select a report file first.'); return; }

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.postForm(`${BASE}/provider-templates/${template.template_id}/test`, fd);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || 'Test request failed.');
        return;
      }
      setResult(data);
    } finally {
      setLoading(false);
    }
  }

  // Portal to document.body: `position: fixed` only anchors to the viewport when
  // NO ancestor is its containing block. Any ancestor with transform, filter,
  // backdrop-filter, perspective, will-change or contain becomes one, and then
  // inset-0 resolves against THAT element's padding box. Measured live on dev:
  // the overlay reported top=16 against innerHeight=945, leaving the top 16px of
  // the page undimmed. Portaling removes every layout ancestor, so inset-0
  // resolves to the viewport again. Same pattern as ui/select.jsx's Radix Portal.
  //
  // overflow-hidden (not overflow-y-auto): a viewport-sized fixed element with
  // overflow set becomes its own scroll container on BOTH axes (per spec the
  // other axis computes from `visible` to `auto`), whose scrollbar shrinks the
  // click-catcher's padding box so clicks near the edge stop dismissing. The
  // panel is capped to the viewport instead.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-16 bg-black/60 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="test-template-modal-title"
    >
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative flex flex-col max-h-[calc(100vh-6rem)] bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-3xl">
        {/* Header — shrink-0 so it keeps its height when the body is squeezed */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-boss-border shrink-0">
          <h2 id="test-template-modal-title" className="text-sm font-semibold text-boss-text">
            Test Template — <span className="text-boss-text-secondary">{template.name}</span>
          </h2>
          <button onClick={onClose} className="text-boss-text-muted hover:text-boss-text-secondary transition">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        {/* Body — the single scroll region. min-h-0 is required: flex children
            default to min-height:auto, which refuses to shrink below content
            size, and a flex child that can't shrink never scrolls. */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          <p className="text-sm text-boss-text-secondary">
            Upload a sample provider report (CSV or Excel) to preview how the template's
            structure recipe and column mappings will transform the first 5 rows.
          </p>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="text-sm text-boss-text-secondary file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-boss-accent-soft file:text-boss-accent hover:file:bg-boss-accent-soft cursor-pointer"
            />
            <button
              type="button"
              onClick={handleTest}
              disabled={loading}
              className="btn-primary text-sm px-4 py-1.5 disabled:opacity-50"
            >
              {loading ? 'Running…' : 'Run Preview'}
            </button>
          </div>

          {error && (
            <div className="px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm">
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
                  result.status === 'success'
                    ? 'bg-boss-status-success-bg text-boss-status-success border-[var(--boss-success-border)]'
                    : 'bg-boss-status-warn-bg text-boss-status-warn border-[var(--boss-warning-border)]'
                }`}>
                  {result.status}
                </span>
                <span className="text-boss-text-secondary">{result.message}</span>
              </div>

              {result.warnings?.length > 0 && (
                <div className="px-3 py-2 rounded-lg bg-boss-status-warn-bg border border-[var(--boss-warning-border)] text-boss-status-warn text-xs space-y-0.5">
                  {result.warnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              )}

              {result.preview?.length > 0 && (
                // No own max-h/overflow: the body above is now the scroller, so a
                // second one here would nest scroll regions (the wheel gets
                // captured by whichever the pointer is over). Dropping the
                // arbitrary 420px cap also lets the preview use the full
                // available height on a large monitor.
                <div className="space-y-3 pr-1">
                  {result.preview.map((row) => (
                    <div key={row.row_index} className="rounded-lg bg-boss-elevated border border-boss-border overflow-hidden">
                      <div className="px-3 py-1.5 border-b border-boss-border text-xs font-medium text-boss-text-secondary">
                        Row {row.row_index + 1}
                      </div>
                      <div className="grid grid-cols-2 divide-x divide-boss-border">
                        <div className="p-3">
                          <p className="text-[10px] text-boss-text-muted uppercase tracking-wide mb-2">Source</p>
                          <pre className="text-[11px] text-boss-text-secondary whitespace-pre-wrap break-all leading-relaxed">
                            {JSON.stringify(row.source, null, 2)}
                          </pre>
                        </div>
                        <div className="p-3">
                          <p className="text-[10px] text-boss-text-muted uppercase tracking-wide mb-2">Extracted</p>
                          <pre className="text-[11px] text-boss-accent whitespace-pre-wrap break-all leading-relaxed">
                            {JSON.stringify(row.extracted, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Preflight Modal — runs preflight against an EXISTING template. The four
// Nextiva templates that motivated this whole feature were all created
// before preflight existed, so this is how an operator checks one after
// the fact instead of only at creation time.
// ---------------------------------------------------------------------------

function PreflightModal({ template, onClose }) {
  const [preflight, setPreflight] = useState({ status: 'idle', checks: [], message: null });
  const [cookiePaste, setCookiePaste] = useState('');
  const [cookieSaving, setCookieSaving] = useState(false);
  const [cookieError, setCookieError] = useState(null);

  const runPreflight = useCallback(async () => {
    setPreflight({ status: 'running', checks: [], message: null });
    setCookieError(null);
    setPreflight(await runProviderTemplatePreflight(template.template_id));
  }, [template.template_id]);

  useEffect(() => { runPreflight(); }, [runPreflight]);

  async function handleCookieSave() {
    if (!cookiePaste.trim()) { setCookieError('Paste your AppDirect session first.'); return; }
    setCookieSaving(true);
    setCookieError(null);
    const result = await saveAppDirectCookie(cookiePaste);
    setCookieSaving(false);
    if (!result.ok) { setCookieError(result.message); return; }
    setCookiePaste('');
    runPreflight();
  }

  // Portal + overflow-hidden + capped panel — see TestTemplateModal above for why.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-16 bg-black/60 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preflight-modal-title"
    >
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative flex flex-col max-h-[calc(100vh-6rem)] bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-boss-border shrink-0">
          <h2 id="preflight-modal-title" className="text-sm font-semibold text-boss-text">
            Preflight — <span className="text-boss-text-secondary">{template.name}</span>
          </h2>
          <button onClick={onClose} className="text-boss-text-muted hover:text-boss-text-secondary transition">
            <X size={16} />
          </button>
        </div>
        {/* min-h-0: flex children default to min-height:auto and refuse to shrink
            below content size, and a child that can't shrink never scrolls. */}
        <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          <p className="text-xs text-boss-text-muted">
            Tests this template against the live AppDirect provider-sales API. Nothing is
            submitted or booked.
          </p>
          <PreflightPanel
            preflight={preflight}
            cookiePaste={cookiePaste}
            onCookiePasteChange={setCookiePaste}
            cookieSaving={cookieSaving}
            cookieError={cookieError}
            onCookieSave={handleCookieSave}
            onRetry={runPreflight}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Edit Modal — create or edit a provider template
// ---------------------------------------------------------------------------

const EDIT_SECTIONS = ['Basic', 'Column Mappings', 'Business Rules'];

function EditTemplateModal({ template, onSuccess, onClose }) {
  const isCreate = !template?.template_id;
  const [form, setForm] = useState(() => template ? templateToForm(template) : emptyTemplate());
  const [activeSection, setActiveSection] = useState('Basic');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showMagic, setShowMagic] = useState(false);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }

    setSaving(true);
    setError(null);
    try {
      const body = formToBody(form);
      const url = isCreate
        ? `${BASE}/provider-templates`
        : `${BASE}/provider-templates/${template.template_id}`;
      const res = isCreate ? await api.post(url, body) : await api.put(url, body);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || 'Failed to save template.');
        return;
      }
      onSuccess();
    } finally {
      setSaving(false);
    }
  }

  function handleMagicApply(suggestions, mode, parseRecipe) {
    setForm((prev) => ({
      ...prev,
      // Recipe travels WITH the mappings it was detected against. When the
      // response had none (flat CSV), keep whatever the template already has
      // — never silently wipe a recipe on a mappings-only merge.
      ...(parseRecipe != null ? { parse_recipe: parseRecipe } : {}),
      column_mappings:
        mode === 'overwrite'
          ? suggestions.map(withMappingUid)
          : [
              ...prev.column_mappings,
              // Merge: skip duplicates by source_column
              ...suggestions
                .filter(
                  (s) => !prev.column_mappings.some((m) => m.source_column === s.source_column)
                )
                .map(withMappingUid),
            ],
    }));
    setShowMagic(false);
  }

  // Portal + overflow-hidden + capped panel — see TestTemplateModal above for the
  // full reasoning. The portal is what removes the undimmed band at the top of
  // the page (measured: overlay top=16 against innerHeight=945 before portaling).
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center p-4 pt-12 bg-black/60 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-template-modal-title"
    >
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative flex flex-col max-h-[calc(100vh-6rem)] bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-boss-border shrink-0">
          <h2 id="edit-template-modal-title" className="text-base font-semibold text-boss-text">
            {isCreate ? 'New Template' : `Edit — ${template.name}`}
          </h2>
          <button onClick={onClose} className="text-boss-text-muted hover:text-boss-text-secondary transition">
            <X size={16} />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-boss-border px-6 shrink-0">
          {EDIT_SECTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setActiveSection(s)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                activeSection === s
                  ? 'border-boss-accent text-boss-accent'
                  : 'border-transparent text-boss-text-secondary hover:text-boss-text'
              }`}
            >
              {s}
              {s === 'Column Mappings' && form.column_mappings.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-boss-elevated border border-boss-border text-boss-text-muted">
                  {form.column_mappings.length}
                </span>
              )}
              {s === 'Business Rules' && form.business_rules.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-boss-elevated border border-boss-border text-boss-text-muted">
                  {form.business_rules.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="px-6 py-5 space-y-4 min-h-[320px] overflow-y-auto flex-1">
            {error && (
              <div className="px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm">
                {error}
              </div>
            )}

            {/* SECTION: Basic */}
            {activeSection === 'Basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-boss-text-secondary mb-1">
                      Name <span className="text-boss-status-danger">*</span>
                    </label>
                    <input
                      className="input-field w-full"
                      required
                      placeholder="e.g. Nextiva Standard"
                      value={form.name}
                      onChange={(e) => setField('name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-boss-text-secondary mb-1">Default Product</label>
                    <input className="input-field w-full" placeholder="UCaaS" value={form.default_product} onChange={(e) => setField('default_product', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-boss-text-secondary mb-1">Default Order Status</label>
                    <input className="input-field w-full" placeholder="PENDING" value={form.default_order_status} onChange={(e) => setField('default_order_status', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-boss-text-secondary mb-1">Sales Rep Priority</label>
                    <input className="input-field w-full" placeholder="Region:East" value={form.sales_rep_priority} onChange={(e) => setField('sales_rep_priority', e.target.value)} />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-boss-text-secondary mb-2">AppDirect FK Overrides</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'appdirect_advisor_id', label: 'Advisor ID' },
                      { key: 'appdirect_product_id', label: 'Product ID' },
                      { key: 'appdirect_provider_id', label: 'Provider ID' },
                      { key: 'appdirect_account_manager_id', label: 'Account Mgr ID' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs text-boss-text-muted mb-1">{label}</label>
                        {/* Text + inputMode numeric, not type=number: these are
                            identifiers, so spinner arrows (and wheel-scroll
                            increments) are meaningless and error-prone.
                            formToBody() coerces via toIntOrNull(). */}
                        <input
                          className="input-field w-full text-xs"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={form[key]}
                          onChange={(e) => setField(key, e.target.value.replace(/[^0-9]/g, ''))}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Structure recipe — read-only summary, shown only when the template has one */}
                {form.parse_recipe && (() => {
                  const label = recipeShortLabel(form.parse_recipe);
                  const { lines } = buildRecipeSummaryLines(form.parse_recipe);
                  if (!label && lines.length === 0) return null;
                  return (
                    <div data-testid="report-structure-recipe-section">
                      <p className="text-xs font-medium text-boss-text-secondary mb-1.5">
                        Structure recipe
                      </p>
                      <div className="rounded-lg border border-boss-border bg-boss-elevated px-3 py-2.5 space-y-1">
                        {label && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border bg-boss-accent-soft text-boss-accent border-boss-border-active"
                            data-testid="report-structure-recipe-chip"
                          >
                            {label}
                          </span>
                        )}
                        {lines.length > 0 && (
                          <ul className="space-y-0.5 mt-1.5">
                            {lines.map((line, i) => (
                              <li key={i} className="text-xs text-boss-text-muted">
                                {line}
                              </li>
                            ))}
                          </ul>
                        )}
                        <p className="text-[10px] text-boss-text-muted mt-1 italic">
                          Read-only — set automatically when the template is created via Magic Mapping.
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* SECTION: Column Mappings */}
            {activeSection === 'Column Mappings' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-boss-text-secondary">
                    Map provider CSV columns to AppDirect Contract H output paths.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowMagic((v) => !v)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-boss-border bg-boss-accent-soft text-boss-accent hover:bg-boss-accent-soft hover:border-boss-border-hover transition"
                  >
                    <Sparkles size={12} />
                    Magic Mapping
                  </button>
                </div>
                {showMagic && (
                  <MagicMappingPanel
                    onApply={handleMagicApply}
                    onClose={() => setShowMagic(false)}
                  />
                )}
                <ColumnMappingsEditor
                  mappings={form.column_mappings}
                  onChange={(v) => setField('column_mappings', v)}
                />
              </div>
            )}

            {/* SECTION: Business Rules */}
            {activeSection === 'Business Rules' && (
              <BusinessRulesEditor
                rules={form.business_rules}
                onChange={(v) => setField('business_rules', v)}
              />
            )}
          </div>

          {/* Footer — pinned outside the scroll region so Save is always reachable */}
          <div className="flex gap-2 px-6 py-4 border-t border-boss-border shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-boss-elevated hover:bg-boss-surface text-boss-text border border-boss-border hover:border-boss-border-hover transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {saving ? 'Saving…' : isCreate ? 'Create Template' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Template Card
// ---------------------------------------------------------------------------

function TemplateCard({ template, canEdit, canDelete, onEdit, onTest, onPreflight, onDelete }) {
  return (
    <div className="glass-card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-boss-text truncate">{template.name}</h3>
          {template.default_product && (
            <p className="text-xs text-boss-text-secondary mt-0.5">{template.default_product}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canEdit && (
            <button
              title="Edit"
              onClick={() => onEdit(template)}
              className="p-1.5 rounded-lg text-boss-text-muted hover:text-boss-accent hover:bg-boss-accent-soft transition"
            >
              <Edit2 size={14} />
            </button>
          )}
          <button
            title="Preflight — test against live AppDirect"
            onClick={() => onPreflight(template)}
            className="p-1.5 rounded-lg text-boss-text-muted hover:text-boss-accent hover:bg-boss-accent-soft transition"
          >
            <ShieldCheck size={14} />
          </button>
          <button
            title="Test with CSV"
            onClick={() => onTest(template)}
            className="p-1.5 rounded-lg text-boss-text-muted hover:text-boss-status-success hover:bg-boss-status-success-bg transition text-xs font-medium"
          >
            Test
          </button>
          {canDelete && (
            <button
              title="Delete"
              onClick={() => onDelete(template)}
              className="p-1.5 rounded-lg text-boss-text-muted hover:text-boss-status-danger hover:bg-boss-status-danger-bg transition"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-boss-text-muted">
        {template.default_order_status && (
          <span className="px-2 py-0.5 rounded bg-boss-elevated border border-boss-border">
            {template.default_order_status}
          </span>
        )}
        <span>{template.column_mappings?.length ?? 0} mapping{template.column_mappings?.length !== 1 ? 's' : ''}</span>
        <span>{template.business_rules?.length ?? 0} rule{template.business_rules?.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProviderTemplatesTab
// ---------------------------------------------------------------------------

export default function ProviderTemplatesTab() {
  const { hasPermission } = usePermissions();
  const canView   = hasPermission('match.provider_template.view');
  const canCreate = hasPermission('match.provider_template.create');
  const canEdit   = hasPermission('match.provider_template.edit');
  const canDelete = hasPermission('match.provider_template.delete');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editTarget, setEditTarget] = useState(null);          // null | {} (new) | template object
  const [testTarget, setTestTarget] = useState(null);          // template object | null
  const [preflightTarget, setPreflightTarget] = useState(null); // template object | null
  const [deleteTarget, setDeleteTarget] = useState(null);      // template object | null

  const loadTemplates = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${BASE}/provider-templates`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load templates.');
      setTemplates(unwrap(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canView]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  async function handleDelete(template) {
    try {
      const res = await api.delete(`${BASE}/provider-templates/${template.template_id}`);
      if (!res.ok) {
        const data = await res.json();
        setError(data?.detail || 'Failed to delete template.');
        return;
      }
      setDeleteTarget(null);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    }
  }

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-48 text-boss-text-secondary text-sm">
        You don't have permission to view provider templates.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-boss-text">Provider Templates</h2>
        {canCreate && (
          <button
            className="btn-primary flex items-center gap-2 text-sm px-3 py-1.5"
            onClick={() => setEditTarget({})}
          >
            <PlusCircle size={15} />
            New Template
          </button>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-boss-status-danger-bg border border-[var(--boss-danger-border)] text-boss-status-danger text-sm">
          {error}
        </div>
      )}

      {/* Card grid */}
      {loading ? (
        <div className="flex items-center justify-center h-36 text-boss-text-secondary text-sm">
          Loading templates…
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-36 gap-2 text-boss-text-secondary text-sm glass-card">
          <span>No provider templates yet.</span>
          {canCreate && (
            <button
              className="text-boss-accent hover:underline text-sm"
              onClick={() => setEditTarget({})}
            >
              Click + New Template to create the first one.
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <TemplateCard
              key={t.template_id}
              template={t}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={setEditTarget}
              onTest={setTestTarget}
              onPreflight={setPreflightTarget}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      {/* Edit / Create Modal */}
      {editTarget !== null && (
        <EditTemplateModal
          template={editTarget.template_id ? editTarget : null}
          onSuccess={() => { setEditTarget(null); loadTemplates(); }}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* Test Modal */}
      {testTarget && (
        <TestTemplateModal
          template={testTarget}
          onClose={() => setTestTarget(null)}
        />
      )}

      {/* Preflight Modal */}
      {preflightTarget && (
        <PreflightModal
          template={preflightTarget}
          onClose={() => setPreflightTarget(null)}
        />
      )}

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete template?"
        message={`Delete "${deleteTarget?.name}"? Sessions already created with this template will retain a reference to the old template ID, but the template record itself will be gone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
